import PropTypes from 'prop-types';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import React, { useEffect, useState, useCallback, useRef, Suspense, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, IconButton, InputAdornment, Tooltip } from '@mui/material';
import axiosInstance from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useAuth } from 'src/hooks/useAuth';
import { useRights } from 'src/hooks/useRights';

/**
 * RHFAutocompleteWithQuickAdd - Autocomplete with API data fetching and quick add functionality
 *
 * FIX: The old implementation used `watch()` with no arguments which subscribes to ALL
 * form fields. When setOptions() ran after a fetch, the re-render recalculated all form
 * values → new formData object → useCallback recreated fetchOptions → useEffect fired
 * again → infinite request loop.
 *
 * Fix: Watch ONLY the parent field (dependsOn prop), same as RHFAutocompleteAsync.
 * Use resolvedEndpoint as the single, stable useEffect dependency.
 */
export default function RHFAutocompleteWithQuickAdd({
    name,
    label,
    apiEndpoint,
    optionLabel = 'name',
    optionValue = '_id',
    placeholder,
    quickAddConfig,
    EntityCreationDialog,
    dependsOn,          // name of the parent/cascading field
    disabled: fieldDisabled, // Extract to prevent auto-clear in edit mode
    ...other
}) {
    const { control, setValue, getValues } = useFormContext();
    const { enqueueSnackbar } = useSnackbar();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const abortRef = useRef(null);

    // Resolve disabled: can be boolean or function
    const isDisabled = typeof fieldDisabled === 'function' ? !!fieldDisabled() : !!fieldDisabled;

    // ── Rights check for the Quick Add button ──────────────────────────────
    // Extract the target entity's module/menu/submenu from its config.
    // useRights must be called unconditionally (Rules of Hooks), so we pass
    // null values when quickAddConfig is absent — useRights returns permissive
    // defaults for null module/menu, and we gate on canShowQuickAdd below.
    const { user } = useAuth();
    const userRole = user?.role;
    const targetEntityRights = quickAddConfig?.formConfig?.entity?.rights;
    const targetModule = targetEntityRights?.module || null;
    const targetMenu   = targetEntityRights?.menu   || null;
    const targetSubmenu = targetEntityRights?.submenu || null;
    const targetRightsResult = useRights(targetModule, targetMenu, targetSubmenu);

    // Determine final visibility of the Quick Add button:
    // • If the target entity has a rights definition → honour canAdd (useRights
    //   auto-bypasses for super_admin / admin).
    // • If the target entity has NO rights definition (it belongs to an admin-only
    //   menu) → restrict to admin / super_admin only.
    const canShowQuickAdd = (() => {
        if (!quickAddConfig) return false;
        if (!targetEntityRights) {
            // Admin-only feature — no moderator access
            return userRole === 'admin' || userRole === 'super_admin';
        }
        return targetRightsResult.canAdd;
    })();

    // Watch ONLY the parent field to avoid subscribing to every form field.
    const parentValue = useWatch({
        control,
        name: dependsOn || '__none__',
        disabled: typeof apiEndpoint !== 'function' || !dependsOn,
    });

    // Resolve endpoint once per parent-value change — stable string/null
    const resolvedEndpoint = (() => {
        if (typeof apiEndpoint !== 'function') return apiEndpoint;
        return apiEndpoint({ [dependsOn]: parentValue });
    })();

    // Exposed so quick-add submit can refresh the list
    const fetchOptions = useCallback(async (endpoint) => {
        if (!endpoint) { setOptions([]); return; }

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const response = await axiosInstance.get(endpoint, { signal: controller.signal });
            const data = response.data?.data || response.data || [];

            const transformed = data.map((item) => {
                let rawLabel = typeof optionLabel === 'function' ? optionLabel(item) : item[optionLabel];
                if (rawLabel && typeof rawLabel === 'object') {
                    rawLabel = rawLabel.name || rawLabel.label || rawLabel.title ||
                        (rawLabel.firstName ? `${rawLabel.firstName} ${rawLabel.lastName || ''}`.trim() :
                            rawLabel.country_name || rawLabel.state_name || rawLabel.city_name || rawLabel.block_name || 'Object');
                }

                return {
                    label: String(rawLabel || item.label || ''),
                    value: item[optionValue] || item._id || item.id,
                    ...item,
                };
            });

            setOptions(transformed);
        } catch (error) {
            if (error.name === 'CanceledError' || error.name === 'AbortError') return;
            console.error('Error fetching autocomplete options:', error.message || error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [optionLabel, optionValue]);

    useEffect(() => {
        // If disabled, skip fetching — existing values display via displayOptions fallback
        if (!isDisabled) {
            fetchOptions(resolvedEndpoint);
        }
        return () => { if (abortRef.current) abortRef.current.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedEndpoint, isDisabled]);

    // Quick add handlers
    const handleQuickAddOpen = useCallback(() => setQuickAddOpen(true), []);
    const handleQuickAddClose = useCallback(() => setQuickAddOpen(false), []);

        const handleQuickAddSubmit = useCallback(async (data) => {
        try {
            const endpoint = quickAddConfig.formConfig.entity.apiEndpoint.create;
            const transformer = quickAddConfig.formConfig.transformDataForSubmit || quickAddConfig.formConfig.features?.transformDataForSubmit || quickAddConfig.formConfig.transformPayload;
            const submitData = transformer
                ? transformer(data)
                : data;

            // Extract formId and projectId from the endpoint query string if it exists and inject it into the payload
            if (endpoint.includes('?')) {
                try {
                    const urlParams = new URLSearchParams(endpoint.split('?')[1]);
                    const formId = urlParams.get('formId');
                    const projectId = urlParams.get('projectId');
                    
                    if (formId) {
                        if (submitData instanceof FormData) {
                            submitData.append('formId', formId);
                        } else {
                            submitData.formId = formId;
                        }
                    }
                    if (projectId) {
                        if (submitData instanceof FormData) {
                            submitData.append('projectId', projectId);
                        } else {
                            submitData.projectId = projectId;
                        }
                    }
                } catch (e) {
                    console.error("Failed to parse query params from endpoint", e);
                }
            }

            const isFormData = submitData instanceof FormData;
            const axiosConfig = isFormData ? { headers: { 'Content-Type': undefined } } : undefined;

            const response = await axiosInstance.post(endpoint, submitData, axiosConfig);
            const newItem = response.data?.data || response.data;

            enqueueSnackbar(`${quickAddConfig.formConfig.entity.name} created successfully`, { variant: 'success' });

            // Refresh options list
            await fetchOptions(resolvedEndpoint);

            // Auto-select the newly created item
            const transformedItem = {
                label: newItem[optionLabel],
                value: newItem[optionValue] || newItem._id || newItem.id,
                ...newItem,
            };
            
            const isMultiple = !!other.multiple;
            if (isMultiple) {
                const prevValues = getValues(name);
                let prevArray = [];
                if (Array.isArray(prevValues)) {
                    prevArray = prevValues;
                } else if (prevValues) {
                    prevArray = [prevValues];
                }
                setValue(name, [...prevArray, transformedItem.value], { shouldValidate: true });
            } else {
                setValue(name, transformedItem.value, { shouldValidate: true });
            }

            handleQuickAddClose();
        } catch (error) {
            console.error('Quick Add Error:', error);
            enqueueSnackbar(error.message || 'Failed to create item', { variant: 'error' });
            throw error;
        }
    }, [quickAddConfig, fetchOptions, resolvedEndpoint, optionLabel, optionValue, name, enqueueSnackbar, handleQuickAddClose, setValue, getValues, other.multiple]);

    const defaultValuesForQuickAdd = useMemo(() => {
        if (!quickAddConfig?.defaultValues) return undefined;
        if (typeof quickAddConfig.defaultValues === 'function') {
            const allFormValues = getValues();
            return quickAddConfig.defaultValues(allFormValues, name);
        }
        return quickAddConfig.defaultValues;
    }, [quickAddConfig, name, getValues]);

    return (
        <>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => {
                    const currentValue = field.value;
                    let displayOptions = [...options];

                    // Ensure current value is in options (edit mode)
                    if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
                        const valueId = currentValue._id || currentValue.id;
                        const existsInOptions = displayOptions.some(opt => {
                            const optId = opt._id || opt.id || opt.value;
                            return optId === valueId;
                        });
                        if (!existsInOptions && valueId) {
                            displayOptions = [currentValue, ...displayOptions];
                        }
                    }

                    if (Array.isArray(currentValue) && currentValue.length > 0) {
                        currentValue.forEach(val => {
                            if (val && typeof val === 'object') {
                                const valueId = val._id || val.id;
                                const existsInOptions = displayOptions.some(opt => {
                                    const optId = opt._id || opt.id || opt.value;
                                    return optId === valueId;
                                });
                                if (!existsInOptions && valueId) {
                                    displayOptions = [val, ...displayOptions];
                                }
                            }
                        });
                    }

                    // Calculate the value for the Autocomplete component securely without nested ternaries
                    let autocompleteValue = null;
                    if (other.multiple) {
                        if (Array.isArray(field.value)) {
                            autocompleteValue = field.value.map(val => {
                                if (typeof val === 'object' && val !== null) return val;
                                return displayOptions.find(opt => (opt.value || opt._id || opt.id) === val) || val;
                            });
                        } else {
                            autocompleteValue = [];
                        }
                    } else if (typeof field.value === 'object' && field.value !== null) {
                        autocompleteValue = field.value;
                    } else {
                        autocompleteValue = displayOptions.find((opt) => (opt.value || opt._id || opt.id) === field.value) || null;
                    }

                    return (
                        <Autocomplete
                            {...field}
                            disabled={isDisabled}
                            options={displayOptions}
                            loading={loading}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                if (!option) return '';
                                if (typeof option.label === 'string') return option.label;

                                let rawLabel = typeof optionLabel === 'function' ? optionLabel(option) : option[optionLabel];
                                if (rawLabel && typeof rawLabel === 'object') {
                                    rawLabel = rawLabel.name || rawLabel.label || rawLabel.title ||
                                        (rawLabel.firstName ? `${rawLabel.firstName} ${rawLabel.lastName || ''}`.trim() :
                                            rawLabel.country_name || rawLabel.state_name || rawLabel.city_name || rawLabel.block_name || 'Object');
                                }
                                return String(rawLabel || '');
                            }}
                            isOptionEqualToValue={(option, value) => {
                                const optVal = option.value || option._id || option.id;
                                const fieldVal = value?.value || value?._id || value?.id || value;
                                return optVal === fieldVal;
                            }}
                            onChange={(event, newValue) => {
                                if (!newValue) {
                                    field.onChange(null);
                                } else if (Array.isArray(newValue)) {
                                    field.onChange(newValue.map(v => v.value || v._id || v.id || v));
                                } else {
                                    field.onChange(newValue.value || newValue._id || newValue.id || newValue);
                                }
                            }}
                            value={autocompleteValue}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={label}
                                    placeholder={placeholder}
                                    error={!!error}
                                    helperText={error?.message}
                                    required={false}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {canShowQuickAdd && !isDisabled && (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={quickAddConfig.buttonLabel || `Add New ${label}`}>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={handleQuickAddOpen}
                                                                size="small"
                                                                sx={{ mr: 1 }}
                                                            >
                                                                <Iconify
                                                                    icon={quickAddConfig.buttonIcon || 'mingcute:add-line'}
                                                                    width={20}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            {...other}
                        />
                    );
                }}
            />

            {canShowQuickAdd && quickAddOpen && EntityCreationDialog && (
                <Suspense fallback={<CircularProgress />}>
                    <EntityCreationDialog
                        open={quickAddOpen}
                        onClose={handleQuickAddClose}
                        onSubmit={handleQuickAddSubmit}
                        title={quickAddConfig.formConfig.form.title.create}
                        titleIcon="mingcute:add-line"
                        submitButtonText="Create"
                        fields={quickAddConfig.formConfig.form.fields}
                        maxWidth={quickAddConfig.formConfig.form.maxWidth}
                        showAvatar={quickAddConfig.formConfig.form.showAvatar}
                        successMessage={`${quickAddConfig.formConfig.entity.name} created successfully`}
                        errorMessage={`Failed to create ${quickAddConfig.formConfig.entity.name}`}
                        defaultValues={defaultValuesForQuickAdd}
                    />
                </Suspense>
            )}
        </>
    );
}

RHFAutocompleteWithQuickAdd.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    apiEndpoint: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    optionValue: PropTypes.string,
    placeholder: PropTypes.string,
    dependsOn: PropTypes.string,
    disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    quickAddConfig: PropTypes.shape({
        formConfig: PropTypes.object.isRequired,
        buttonLabel: PropTypes.string,
        buttonIcon: PropTypes.string,
        defaultValues: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    }),
    EntityCreationDialog: PropTypes.elementType,
};
